import React from 'react';
import { IonText } from '@ionic/react';
import {
  INVENTORY_OPTIONS_PRODUCT_GROUP_ID,
  INVENTORY_COLORS_PRODUCT_GROUP_ID
} from 'core/constants';
import type { ProductGroupType, ProductTemplateType } from 'core/types';
import HKSelect from 'components/hk-select/HKSelect';

export interface ProductGroupTypeSelectsProps {
  /** Class prefix shared by every element below, e.g. 'hk-desktop-digital-add-product-body'. */
  classPrefix: string;
  productTypeGroupsList: ProductGroupType[];
  selectedProductGroup: number | null | undefined;
  selectedProductType: number | null | undefined;
  onGroupChange: (groupId: number | null) => void;
  onTypeChange: (typeId: number | null) => void;
}

/**
 * Shared Group -> Type cascade for the "Add an Item" flow (desktop + mobile),
 * pulled out of InventoryAddProduct.tsx's two near-identical copies (one per
 * platform, differing only by class prefix). Callers own the actual state
 * resets triggered by a change (see onGroupChange/onTypeChange call sites in
 * InventoryAddProduct.tsx) — this component only renders and reports which
 * id was picked.
 */
const ProductGroupTypeSelects: React.FC<ProductGroupTypeSelectsProps> = ({
  classPrefix,
  productTypeGroupsList,
  selectedProductGroup,
  selectedProductType,
  onGroupChange,
  onTypeChange
}) => {
  const groupOptions = productTypeGroupsList
    .filter(
      (group: ProductGroupType) =>
        group.id !== INVENTORY_OPTIONS_PRODUCT_GROUP_ID &&
        group.id !== INVENTORY_COLORS_PRODUCT_GROUP_ID
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((group: ProductGroupType) => ({ value: group.id, label: group.name }));

  const typeOptions = [
    ...(productTypeGroupsList.find((group) => group.id === selectedProductGroup)
      ?.product_template_types ?? [])
  ]
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((productType: ProductTemplateType) => ({
      value: productType.id,
      label: productType.label
    }));

  return (
    <div>
      <IonText className={`${classPrefix}-subtext`}>
        Select a group and type to add an item.
      </IonText>
      <HKSelect
        label="Group"
        className={`${classPrefix}-select-product`}
        labelClassName={`${classPrefix}-select-product-label`}
        placeholder="Select item group"
        value={selectedProductGroup ?? null}
        options={groupOptions}
        onChange={(value) => onGroupChange(value as number | null)}
      />
      <HKSelect
        label="Type"
        className={`${classPrefix}-select-product`}
        labelClassName={`${classPrefix}-select-product-label`}
        placeholder="Select item type"
        value={selectedProductType ?? null}
        disabled={!selectedProductGroup}
        options={typeOptions}
        onChange={(value) => onTypeChange(value as number | null)}
      />
    </div>
  );
};

export default ProductGroupTypeSelects;
